import { useEffect, useMemo, useState } from 'react';

import { useGetProductQuery } from '../api/store/apiSlice';
import { useRootSelector } from '../../store';

import {
  selectProductTag,
  selectSlots,
  selectStyle
} from '../item/store/itemSlice';
import { Icon } from '../../types';
import { CUSTOMER_API_URL } from '../../config';

let defaultIconCache: Icon | null = null;
let defaultIconPromise: Promise<Icon> | null = null;

export function useDefaultIcon() {
  const [defaultIcon, setDefaultIcon] = useState<Icon | null>(defaultIconCache);
  const [isLoading, setIsLoading] = useState(!defaultIconCache);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (defaultIconCache) {
      setDefaultIcon(defaultIconCache);
      return;
    }

    if (defaultIconPromise) {
      defaultIconPromise
        .then(icon => {
          setDefaultIcon(icon);
          setIsLoading(false);
        })
        .catch(err => {
          setError(err.message);
          setIsLoading(false);
        });
      return;
    }

    setIsLoading(true);
    defaultIconPromise = fetch(`${CUSTOMER_API_URL}/icons/default`)
      .then(response => {
        if (!response.ok) {
          throw new Error('Failed to fetch default icon');
        }
        return response.json();
      })
      .then(icon => {
        defaultIconCache = icon;
        return icon;
      });

    defaultIconPromise
      .then(icon => {
        setDefaultIcon(icon);
        setIsLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setIsLoading(false);
      });
  }, []);

  return { defaultIcon, isLoading, error };
}

export function useIconsAndLabels() {
  const { defaultIcon } = useDefaultIcon();
  const productTag = useRootSelector(selectProductTag);
  const { data, isLoading, isSuccess, isError, error } =
    useGetProductQuery(productTag);

  const selectedStyle = useRootSelector(selectStyle);
  const slots = useRootSelector(selectSlots);

  /* Icons.
   * This step should only appear if one or more labels within the product has
   * an icon layer included AND the currently selected variants support icons
   */
  const showIcons = useMemo(() => {
    // labels on this product
    const productLabelIds = new Set(
      data?.label_slots?.flatMap(
        labelSlot =>
          labelSlot?.slot_options?.flatMap(slotOption => slotOption.label_id)
      )
    );

    // labels that have one of the above ids AND have A variant with an icon
    // layer - and that is in the style.
    const labels =
      data?.labels
        ?.filter(label => productLabelIds?.has(label.label_id))
        ?.filter(label =>
          label.variants.some(
            v => v.style_id === selectedStyle?.style_id && v.icons.length > 0
          )
        ) ?? [];

    const styleIcons = (selectedStyle?.icon_ids?.length ?? 0) > 1;

    // Check if any of the currently selected variants support icons
    // For multipack products, we want to show icons if ANY label supports them
    const anySelectedVariantSupportsIcons = (() => {
      if (!data?.label_slots || !selectedStyle) return false;

      return data.label_slots.some(labelSlot => {
        const slot = slots?.[labelSlot.product_label_slot_id];
        if (!slot?.labelId || !slot?.variantId) return false;

        const label = data.labels?.find(l => l.label_id === slot.labelId);
        if (!label) return false;

        const selectedVariant = label.variants?.find(
          v =>
            v.label_variant_id === slot.variantId &&
            v.style_id === selectedStyle.style_id
        );

        return (selectedVariant?.icons?.length ?? 0) > 0;
      });
    })();

    return styleIcons && labels.length > 0 && anySelectedVariantSupportsIcons;
  }, [data, selectedStyle, slots]);

  /**
   * Depending on the style selected, there may be multiple label variants for
   * users to select from. If there is a label within the product that has
   * multiple label variants for the same style AND the style is not “Iterative”
   * then users will have a choice between the options at hand. If no labels
   * within the product meet this condition, the step should be hidden
   */

  const labelHasMultipleVariants = useMemo(() => {
    const labelVariantMap = {} as { [key: string]: boolean };
    if (slots && data) {
      data.label_slots?.forEach(labelSlot => {
        const labelId = slots?.[labelSlot.product_label_slot_id]?.labelId;

        const variants = data?.labels
          ?.find(label => label.label_id === labelId)
          ?.variants?.filter(
            variant => variant.style_id === selectedStyle?.style_id
          );

        labelVariantMap[labelId || ''] = (variants?.length ?? 0) > 1;
      });
    }
    return labelVariantMap;
  }, [slots, data, selectedStyle?.style_id]);

  const showLabels = useMemo(
    () =>
      data?.label_slots?.some(labelSlot => {
        const labelId = slots?.[labelSlot.product_label_slot_id]?.labelId;
        const label = data.labels.find(l => l.label_id === labelId);
        const isIterative = label?.iterative_style_ids?.includes(
          selectedStyle?.style_id ?? ''
        );
        const hasMultipleVariants = labelHasMultipleVariants[labelId || ''];
        return !(isIterative || !hasMultipleVariants);
      }),
    [data, labelHasMultipleVariants, slots, selectedStyle?.style_id]
  );

  const hideIconsAndLabels = useMemo(
    () => !showLabels && !showIcons,
    [showLabels, showIcons]
  );

  const getDefaultIcon = useMemo(() => {
    return defaultIcon || null;
  }, [defaultIcon]);

  return {
    showIcons,
    showLabels,
    hideIconsAndLabels,
    labelHasMultipleVariants,
    getDefaultIcon,
    isLoading,
    isSuccess,
    isError,
    error
  };
}
